/**
 * Sub-components extracted from SahibCompanion to keep the main file
 * readable (was 833 lines pre-refactor, target ~600 after).
 *
 * Each card is a pure presentation component — no API calls, no state.
 * The parent passes everything it needs via props + callbacks.
 *
 * Why this lives in a sibling file (not its own per-card files): every
 * card is < 60 lines, deeply coupled to SahibCompanion's message-row
 * styling (mr-8, the gold/purple chrome), and never reused elsewhere.
 * Co-locating beats six near-identical files.
 */
import React from 'react';
import {
  Check, Ban, Loader2, Clock, ShieldQuestion, CheckCircle2,
  Camera, Image as ImageIcon, X,
} from 'lucide-react';

// ─── ToolConfirmCard ────────────────────────────────────────────────
// Renders when an Iqra/persona tool-call needs human approval before
// it can run (book_resource, book_meeting_from_query, etc.).
export function ToolConfirmCard({ pending, onApprove, onDecline, busy }) {
  const isBookResource = pending.tool === 'book_resource' && pending.args?.request;
  const isBookMeeting = pending.tool === 'book_meeting_from_query' && pending.args?.query;
  const hasGenericArgs = !isBookResource && !isBookMeeting && Object.keys(pending.args || {}).length > 0;
  return (
    <div
      className="mt-2 mr-8 rounded-lg border-2 border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/70 p-3"
      data-testid={`tool-confirm-${pending.tool}`}
    >
      <div className="flex items-start gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A5A00] dark:text-[#8B3FC5]">
        <ShieldQuestion size={12} className="mt-0.5"/>
        Confirm action · <span className="normal-case tracking-normal font-bold ml-1">{pending.tool}</span>
      </div>
      <div className="mt-2 text-sm text-slate-800 dark:text-white/90 leading-snug">
        {pending.confirmation_text}
      </div>
      {isBookResource && (
        <RequestEcho
          label="Your request"
          quote={pending.args.request}
          note="On approve I’ll parse this, check for room/asset conflicts, and write the booking."
        />
      )}
      {isBookMeeting && (
        <RequestEcho
          label="Your request"
          quote={pending.args.query}
          note="On approve I’ll query the entities + startups in your tenant, find every match, and draft one meeting with their primary contacts as invitees."
        />
      )}
      {hasGenericArgs && (
        <pre className="mt-2 text-[10px] mono bg-white/60 dark:bg-black/30 rounded p-2 overflow-x-auto text-slate-700 dark:text-white/70">
{JSON.stringify(pending.args, null, 2)}
        </pre>
      )}
      <div className="mt-3 flex gap-2">
        <button
          onClick={onApprove}
          disabled={busy}
          data-testid={`tool-approve-${pending.tool}`}
          className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md bg-[#0A0A0A] text-[#8B3FC5] hover:bg-[#1a1a1a] disabled:opacity-40 transition"
        >
          <Check size={11}/> Approve
        </button>
        <button
          onClick={onDecline}
          disabled={busy}
          data-testid={`tool-decline-${pending.tool}`}
          className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-300 text-slate-700 dark:border-white/15 dark:text-white/65 hover:border-rose-400 hover:text-rose-500 disabled:opacity-40 transition"
        >
          <Ban size={11}/> Cancel
        </button>
      </div>
    </div>
  );
}

function RequestEcho({ label, quote, note }) {
  return (
    <div className="mt-3 rounded border border-[#8B3FC5]/40 bg-white/60 dark:bg-black/30 px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] mb-1">{label}</div>
      <div className="text-xs text-slate-800 dark:text-white/85 italic">&ldquo;{quote}&rdquo;</div>
      <div className="mt-2 text-[10px] text-slate-500 dark:text-white/55">{note}</div>
    </div>
  );
}

// ─── RunningPlanCard ────────────────────────────────────────────────
// Live progress of a multi-step plan as Iqra executes each step.
export function RunningPlanCard({ plan }) {
  const done = plan.steps.filter((s) => s.status === 'done').length;
  return (
    <div
      className="mt-2 mr-8 rounded-lg border border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/70 p-3"
      data-testid={`plan-running-${plan.plan_id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A5A00] dark:text-[#8B3FC5]">
        <Loader2 size={12} className="animate-spin"/>
        Running plan · {done}/{plan.steps.length} done
      </div>
      <ol className="mt-2 space-y-1.5">
        {plan.steps.map((s, si) => (
          <li key={`${si}-${s.tool}`} className="flex items-start gap-2 text-xs">
            <span className="mono shrink-0 text-[10px] mt-0.5 w-4 h-4 inline-flex items-center justify-center">
              {s.status === 'queued' && <Clock size={11} className="text-slate-400"/>}
              {s.status === 'running' && <Loader2 size={11} className="animate-spin text-[#5E1F8B]"/>}
              {s.status === 'done' && <Check size={11} className="text-emerald-500"/>}
              {s.status === 'failed' && <Ban size={11} className="text-rose-500"/>}
            </span>
            <span className="flex-1 text-slate-700 dark:text-white/80 leading-snug">
              <span className="font-bold">{s.tool}</span>
              {s.rationale && <span className="text-slate-500 dark:text-white/55"> — {s.rationale}</span>}
              {s.status === 'done' && s.result && (
                <div className="mt-1 text-[11px] text-slate-600 dark:text-white/70 whitespace-pre-wrap">{s.result}</div>
              )}
              {s.status === 'failed' && s.error && (
                <div className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">{s.error}</div>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─── PendingPlanCard ────────────────────────────────────────────────
// Awaiting human approval for a multi-step plan with mixed read/write.
export function PendingPlanCard({ plan, onApprove, onDecline, busy }) {
  return (
    <div
      className="mt-2 mr-8 rounded-lg border-2 border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/70 p-3"
      data-testid={`plan-confirm-${plan.plan_id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A5A00] dark:text-[#8B3FC5]">
        <ShieldQuestion size={12}/>
        Multi-step plan · {plan.steps.length} step{plan.steps.length === 1 ? '' : 's'}
        {plan.contains_writes && (
          <span className="ml-1 normal-case tracking-normal text-rose-600 dark:text-rose-400 font-bold">
            (writes data)
          </span>
        )}
      </div>
      <div className="mt-2 text-sm text-slate-800 dark:text-white/90 leading-snug">
        {plan.confirmation_text}
      </div>
      <ol className="mt-2 space-y-1.5">
        {plan.steps.map((s, si) => (
          <li key={`${si}-${s.tool}`} className="flex items-start gap-2 text-xs">
            <span className="mono shrink-0 text-[10px] mt-0.5 px-1.5 h-4 rounded bg-[#0A0A0A] text-[#8B3FC5] inline-flex items-center">{si + 1}</span>
            <span className="flex-1 text-slate-700 dark:text-white/80 leading-snug">
              <span className="font-bold">{s.tool}</span>
              {s.kind === 'write' && (
                <span className="ml-1 text-[9px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400">write</span>
              )}
              {s.rationale && <span className="text-slate-500 dark:text-white/55"> — {s.rationale}</span>}
              {Object.keys(s.args || {}).length > 0 && (
                <span className="block mono text-[10px] text-slate-400 dark:text-white/40 truncate">
                  {JSON.stringify(s.args)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
      <div className="mt-3 flex gap-2">
        <button
          onClick={onApprove}
          disabled={busy}
          data-testid={`plan-approve-${plan.plan_id}`}
          className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md bg-[#0A0A0A] text-[#8B3FC5] hover:bg-[#1a1a1a] disabled:opacity-40 transition"
        >
          <Check size={11}/> Approve all
        </button>
        <button
          onClick={onDecline}
          disabled={busy}
          data-testid={`plan-decline-${plan.plan_id}`}
          className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border border-slate-300 text-slate-700 dark:border-white/15 dark:text-white/65 hover:border-rose-400 hover:text-rose-500 disabled:opacity-40 transition"
        >
          <Ban size={11}/> Cancel
        </button>
      </div>
    </div>
  );
}

// ─── JournalInputPanel ──────────────────────────────────────────────
// Iqra-only screenshot + feedback-journal capture. Lives above the
// chat input.
export function JournalInputPanel({
  shotData, shotBusy, journalSent, busy, msg,
  onCapture, onClearShot, onSend,
}) {
  return (
    <div className="rounded-md border border-dashed border-[#8B3FC5]/40 bg-[#FFF8E5]/30 dark:bg-[#3a2f0a]/20 p-2.5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A5A00] dark:text-[#8B3FC5]">
        <Camera size={11}/> Feedback Journal · Iqra
        {journalSent && (
          <span className="ml-auto inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 normal-case tracking-normal">
            <CheckCircle2 size={11}/> Logged
          </span>
        )}
      </div>
      {shotData ? (
        <div className="mt-2 relative group">
          <img
            src={shotData}
            alt="Captured screenshot"
            className="w-full max-h-40 object-contain rounded border border-slate-200 dark:border-white/10"
          />
          <button
            onClick={onClearShot}
            className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
            data-testid="sahib-shot-clear"
            title="Remove screenshot"
          >
            <X size={12}/>
          </button>
        </div>
      ) : (
        <button
          onClick={onCapture}
          disabled={shotBusy}
          data-testid="sahib-shot-capture"
          className="mt-2 w-full inline-flex items-center justify-center gap-2 h-9 text-xs uppercase tracking-[0.2em] font-bold
                     border border-[#8B3FC5]/60 rounded-md text-[#7A5A00] dark:text-[#8B3FC5]
                     hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a]/50 transition disabled:opacity-50"
        >
          {shotBusy ? <Loader2 size={12} className="animate-spin"/> : <Camera size={12}/>}
          Capture screen
        </button>
      )}
      <button
        onClick={onSend}
        disabled={busy || (!msg.trim() && !shotData)}
        data-testid="sahib-journal-send"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 h-9 text-xs uppercase tracking-[0.2em] font-bold
                   bg-[#0A0A0A] text-[#8B3FC5] rounded-md hover:bg-[#141414] transition disabled:opacity-40"
      >
        <ImageIcon size={12}/> Log to journal
      </button>
      <div className="mt-1.5 text-[10px] text-slate-500 dark:text-white/45 leading-snug">
        Every entry — your note + the screenshot — appends to the admin&rsquo;s Feedback Journal continuously.
      </div>
    </div>
  );
}

// ─── MessageStatusBadges ────────────────────────────────────────────
// Inline status pills appended below a chat message after a tool /
// plan is approved, declined, halted, etc. Pure props → markup.
export function MessageStatusBadges({ msg }) {
  return (
    <>
      {msg.confirmed && (
        <BadgePill testid="msg-executed" tone="emerald" icon={<CheckCircle2 size={10}/>}>Executed</BadgePill>
      )}
      {msg.declined && (
        <BadgePill testid="msg-cancelled" tone="slate" icon={<Ban size={10}/>}>Cancelled</BadgePill>
      )}
      {msg.planConfirmed && (
        <BadgePill testid="msg-plan-executed" tone="emerald" icon={<CheckCircle2 size={10}/>}>Plan executed</BadgePill>
      )}
      {msg.planHalted && (
        <BadgePill testid="msg-plan-halted" tone="rose" icon={<Ban size={10}/>}>Plan halted</BadgePill>
      )}
      {msg.planDeclined && (
        <BadgePill testid="msg-plan-cancelled" tone="slate" icon={<Ban size={10}/>}>Plan cancelled</BadgePill>
      )}
    </>
  );
}

const TONE = {
  emerald: 'text-emerald-600 dark:text-emerald-400',
  slate: 'text-slate-500 dark:text-white/55',
  rose: 'text-rose-600 dark:text-rose-400',
};

function BadgePill({ children, tone, icon, testid }) {
  return (
    <div className={`mt-1 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 ml-2 ${TONE[tone] || TONE.slate}`} data-testid={testid}>
      {icon}{children}
    </div>
  );
}
